import type { Feature } from "@/server/types/billing";

export const EHR_PROVIDER_SECTIONS = [
  { key: "patient-access", label: "Patient Access" },
  { key: "clinical-workflows", label: "Clinical Workflows" },
  { key: "backend", label: "Backend" },
  { key: "network", label: "Network" },
] as const;

/** Section keys used to group provider tiles in the Connections settings list. */
export type EhrSectionKey = (typeof EHR_PROVIDER_SECTIONS)[number]["key"];

/** Which paid feature (if any) gates each connections sub-section. Patient Access and Network are always open. */
export const EHR_SECTION_REQUIRED_FEATURE: Partial<Record<EhrSectionKey, Feature>> = {
  "clinical-workflows": "clinical_workflows",
  backend: "backend_services",
};

export const EHR_PROVIDERS = [
  {
    type: "epic" as const,
    section: "patient-access" as const,
    label: "Epic",
    logo: "epic/epic_logo.png",
    registrationUrl: "https://fhir.epic.com/Developer/Apps",
    scopes: [
      "openid",
      "fhirUser",
      "patient/Observation.read",
      "patient/Patient.read",
      "offline_access",
    ],
    description:
      "Epic is used by major health systems and hospitals across the US. Connecting allows Medblocks to securely retrieve your patients' clinical records via SMART on FHIR.",
    supportedResources: [
      "Patient demographics",
      "Conditions & diagnoses",
      "Medications",
      "Lab results",
      "Allergies",
      "Immunizations",
      "Procedures",
      "Clinical documents",
    ],
  },
  {
    type: "cerner" as const,
    section: "patient-access" as const,
    label: "Cerner",
    logo: "cerner/cerner_logo.png",
    registrationUrl: "https://code.cerner.com/developer/smart-on-fhir/apps",
    scopes: [
      "openid",
      "fhirUser",
      "offline_access",
      "patient/Patient.read",
      "patient/AllergyIntolerance.read",
      "patient/CarePlan.read",
      "patient/CareTeam.read",
      "patient/Condition.read",
      "patient/Device.read",
      "patient/DiagnosticReport.read",
      "patient/DocumentReference.read",
      "patient/Encounter.read",
      "patient/Goal.read",
      "patient/Immunization.read",
      "patient/MedicationRequest.read",
      "patient/Observation.read",
      "patient/Procedure.read",
      "patient/ServiceRequest.read",
      "patient/Coverage.read",
      "patient/Binary.read",
      "patient/RelatedPerson.read",
    ],
    description:
      "Cerner (Oracle Health) is an enterprise EHR platform used across hospitals and health networks. Connecting enables access to patient clinical data via SMART on FHIR R4.",
    supportedResources: [
      "Patient demographics",
      "Conditions",
      "Medications",
      "Observations & vitals",
      "Encounter history",
      "Allergies",
      "Procedures",
    ],
  },
  {
    type: "athena" as const,
    section: "patient-access" as const,
    label: "athenahealth",
    logo: "athena/athenahealth_logo.png",
    registrationUrl: "https://developer.athenahealth.com/",
    scopes: [
      "openid",
      "fhirUser",
      "offline_access",
      "launch/patient",
      "patient/AllergyIntolerance.rs",
      "patient/Binary.rs",
      "patient/CarePlan.rs",
      "patient/CareTeam.rs",
      "patient/Condition.rs",
      "patient/Coverage.rs",
      "patient/Device.rs",
      "patient/DiagnosticReport.rs",
      "patient/DocumentReference.rs",
      "patient/Encounter.rs",
      "patient/Goal.rs",
      "patient/Immunization.rs",
      "patient/Location.rs",
      "patient/Media.rs",
      "patient/Medication.rs",
      "patient/MedicationDispense.rs",
      "patient/MedicationRequest.rs",
      "patient/Observation.rs",
      "patient/Organization.rs",
      "patient/Patient.rs",
      "patient/Practitioner.rs",
      "patient/Procedure.rs",
      "patient/Provenance.rs",
      "patient/QuestionnaireResponse.rs",
      "patient/RelatedPerson.rs",
      "patient/ServiceRequest.rs",
      "patient/Specimen.rs",
    ],
    description:
      "athenahealth is a cloud-based EHR widely used in ambulatory and outpatient care. Connecting allows retrieval of patient records from athenahealth-powered practices.",
    supportedResources: [
      "Patient demographics",
      "Appointments",
      "Medications",
      "Conditions",
      "Clinical documents",
      "Lab results",
    ],
  },
  {
    type: "eclinicalworks" as const,
    section: "patient-access" as const,
    label: "eClinicalWorks",
    logo: "ecw/ecw_logo.png",
    registrationUrl: "https://developer.eclinicalworks.com/",
    scopes: [
      "openid",
      "fhirUser",
      "offline_access",
      "patient/AllergyIntolerance.read",
      "patient/Binary.read",
      "patient/CarePlan.read",
      "patient/CareTeam.read",
      "patient/Condition.read",
      "patient/Device.read",
      "patient/DiagnosticReport.read",
      "patient/DocumentReference.read",
      "patient/Encounter.read",
      "patient/FamilyMemberHistory.read",
      "patient/Goal.read",
      "patient/Immunization.read",
      "patient/Location.read",
      "patient/Medication.read",
      "patient/MedicationAdministration.read",
      "patient/MedicationRequest.read",
      "patient/Observation.read",
      "patient/Organization.read",
      "patient/Patient.read",
      "patient/Practitioner.read",
      "patient/PractitionerRole.read",
      "patient/Procedure.read",
      "patient/Provenance.read",
    ],
    description:
      "eClinicalWorks is an ambulatory EHR and practice management platform. Connecting enables access to patient visit history and clinical records from eCW-powered practices.",
    supportedResources: [
      "Patient demographics",
      "Visit history",
      "Medications",
      "Conditions",
      "Allergies",
      "Immunizations",
    ],
  },
  {
    type: "uhc" as const,
    section: "patient-access" as const,
    label: "UnitedHealthcare",
    logo: "unitedhealth/united_health_logo.png",
    registrationUrl: "https://portal.flex.optum.com/",
    scopes: [
      "patient/Patient.read",
      "patient/Coverage.read",
      "patient/ExplanationOfBenefit.read",
      "patient/Condition.read",
      "patient/Encounter.read",
      "patient/Procedure.read",
      "patient/MedicationRequest.read",
      "patient/MedicationDispense.read",
      "patient/Observation.read",
      "patient/Immunization.read",
    ],
    description:
      "UnitedHealthcare exposes the CMS-mandated Patient Access API on Optum's FLEX platform. Connecting lets members securely share their payer-held data — coverage, claims (Explanation of Benefit) and clinical records — via SMART on FHIR R4.",
    supportedResources: [
      "Patient demographics",
      "Insurance coverage",
      "Claims & Explanation of Benefit",
      "Conditions & diagnoses",
      "Encounters",
      "Procedures",
      "Medications",
      "Observations & lab results",
      "Immunizations",
    ],
  },
  {
    type: "medicare" as const,
    section: "patient-access" as const,
    label: "Medicare",
    logo: "cms-bluebutton/cms_bluebutton_logo.webp",
    registrationUrl: "https://bluebutton.cms.gov/developers/",
    scopes: [
      "patient/Patient.read",
      "patient/Coverage.read",
      "patient/ExplanationOfBenefit.read",
      "profile",
    ],
    description:
      "Lets Medicare enrollees share their claims, coverage, and Explanation of Benefit data securely via SMART on FHIR.",
    supportedResources: [
      "Patient demographics",
      "Coverage",
      "Explanation of Benefit (claims)",
    ],
  },
  {
    type: "smart-health-it" as const,
    section: "patient-access" as const,
    label: "SMART Health IT Sandbox",
    logo: "smart-health-it/smart-health-it_logo.svg",
    registrationUrl: "https://launch.smarthealthit.org/",
    scopes: ["openid", "fhirUser", "launch/patient", "patient/*.read", "offline_access"],
    description:
      "SMART Health IT's public reference SMART on FHIR sandbox. Anonymous test EHR with a built-in patient picker; useful for conformance testing without needing vendor credentials.",
    supportedResources: [
      "Patient demographics",
      "Conditions",
      "Medications",
      "Lab results & vitals",
      "Immunizations",
    ],
  },
  {
    type: "aetna" as const,
    section: "patient-access" as const,
    label: "Aetna",
    logo: "aetna/aetna_logo.jpg",
    registrationUrl: "https://developerportal.aetna.com/",
    scopes: ["launch/patient", "patient/*.read"],
    description:
      "Lets Aetna members share their clinical and claims data via the CMS Patient Access API.",
    supportedResources: [
      "Patient demographics",
      "Coverage",
      "Explanation of Benefit (claims)",
      "Conditions",
      "Encounters",
      "Medications",
      "Observations",
      "Procedures",
      "Immunizations",
      "Care plans & care teams",
      "Allergies",
    ],
  },
  {
    type: "humana" as const,
    section: "patient-access" as const,
    label: "Humana",
    logo: "humana/humana_logo.png",
    registrationUrl: "",
    scopes: [],
    description: "Humana integration for patient-mediated health data exchange.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "meditech" as const,
    section: "patient-access" as const,
    label: "MEDITECH",
    logo: "meditech/meditech_logo.png",
    registrationUrl: "https://ehr.meditech.com/meditech-greenfield",
    scopes: ["openid", "fhirUser", "launch/patient", "offline_access", "patient/*.read"],
    description:
      "MEDITECH Expanse is used by 2,300+ hospitals worldwide. Connecting allows Medblocks to retrieve patient records via Greenfield US Core FHIR R4 (SMART Standalone Launch).",
    supportedResources: [
      "Patient demographics",
      "Conditions",
      "Medications",
      "Lab results & vitals",
      "Allergies",
      "Immunizations",
      "Procedures",
      "Encounters",
      "Clinical documents",
    ],
  },
  {
    type: "modmed" as const,
    section: "patient-access" as const,
    label: "ModMed",
    logo: "modmed/modmed_square.jpeg",
    registrationUrl: "",
    scopes: [],
    description: "ModMed (Modernizing Medicine) integration for patient-mediated data exchange.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "idme" as const,
    section: "patient-access" as const,
    label: "TEFCA Individual Access Services",
    logo: "idme/idme_logo.png",
    registrationUrl: "",
    scopes: [],
    description:
      "ID.me Individual Access Services (IAS) under TEFCA enables patients to retrieve their health records nationwide through the Trusted Exchange Framework.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "epic-clinical-workflow" as const,
    section: "clinical-workflows" as const,
    label: "Epic",
    logo: "epic/epic_logo.png",
    registrationUrl: "https://fhir.epic.com/Developer/Apps",
    scopes: [
      "openid",
      "fhirUser",
      "launch",
      "patient/Patient.read",
      "patient/Observation.read",
      "online_access",
    ],
    description:
      "Launch Medblocks as a SMART on FHIR app embedded directly inside the Epic clinician workflow. Providers can open Medblocks in-context from a patient's chart without leaving Epic.",
    supportedResources: [
      "Patient context (launch)",
      "Encounter context",
      "Clinical documents",
    ],
  },
  {
    type: "cerner-clinical-workflow" as const,
    section: "clinical-workflows" as const,
    label: "Cerner",
    logo: "cerner/cerner_logo.png",
    registrationUrl: "https://code.cerner.com/developer/smart-on-fhir/apps",
    scopes: [
      "openid",
      "fhirUser",
      "launch",
      "user/Patient.read",
      "user/Encounter.read",
      "user/Observation.read",
      "online_access",
    ],
    description:
      "Launch Medblocks as a SMART on FHIR app embedded directly inside the Cerner (Oracle Health) clinician workflow. Providers can open Medblocks in-context from a patient's chart without leaving PowerChart.",
    supportedResources: [
      "Patient context (launch)",
      "Encounter context",
      "Clinical documents",
    ],
  },
  {
    type: "athena-clinical-workflow" as const,
    section: "clinical-workflows" as const,
    label: "athenahealth",
    logo: "athena/athenahealth_logo.png",
    registrationUrl: "",
    scopes: [],
    description: "Embedded clinical-workflow launch for athenahealth.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "eclinicalworks-clinical-workflow" as const,
    section: "clinical-workflows" as const,
    label: "eClinicalWorks",
    logo: "ecw/ecw_logo.png",
    registrationUrl: "",
    scopes: [],
    description: "Embedded clinical-workflow launch for eClinicalWorks.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "meditech-clinical-workflow" as const,
    section: "clinical-workflows" as const,
    label: "MEDITECH",
    logo: "meditech/meditech_logo.jpeg",
    registrationUrl: "",
    scopes: [],
    description: "Embedded clinical-workflow launch for MEDITECH.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "modmed-clinical-workflow" as const,
    section: "clinical-workflows" as const,
    label: "ModMed",
    logo: "modmed/modmed_square.jpeg",
    registrationUrl: "",
    scopes: [],
    description: "Embedded clinical-workflow launch for ModMed.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "epic-backend" as const,
    section: "backend" as const,
    label: "Epic",
    logo: "epic/epic_logo.png",
    registrationUrl: "https://fhir.epic.com/Developer/Apps",
    scopes: [
      "system/Patient.read",
      "system/Group.read",
      "system/Observation.read",
      "system/Condition.read",
    ],
    description:
      "Pull data from Epic at the population level via SMART Backend Services (system-level access, no user). Configure the Epic organizations you connect to and the FHIR Group IDs to export for each.",
    supportedResources: [
      "Group-level bulk export",
      "Patient demographics",
      "Conditions & diagnoses",
      "Observations",
    ],
  },
  {
    type: "cerner-backend" as const,
    section: "backend" as const,
    label: "Cerner",
    logo: "cerner/cerner_logo.png",
    registrationUrl: "https://code.cerner.com/developer/smart-on-fhir/apps",
    scopes: [
      "system/AllergyIntolerance.read",
      "system/Binary.read",
      "system/CarePlan.read",
      "system/CareTeam.read",
      "system/Condition.read",
      "system/Coverage.read",
      "system/Device.read",
      "system/DiagnosticReport.read",
      "system/DocumentReference.read",
      "system/Encounter.read",
      "system/Goal.read",
      "system/Immunization.read",
      "system/Location.read",
      "system/MedicationRequest.read",
      "system/Observation.read",
      "system/Organization.read",
      "system/Patient.read",
      "system/Practitioner.read",
      "system/Procedure.read",
      "system/RelatedPerson.read",
      "system/ServiceRequest.read",
    ],
    description:
      "Pull data from Cerner (Oracle Health) at the population level via SMART Backend Services (system-level access, no user). Configure the Cerner tenants you connect to and the FHIR Group IDs to export for each.",
    supportedResources: [
      "Group-level bulk export",
      "Patient demographics",
      "Allergies",
      "Care plans & care teams",
      "Conditions",
      "Coverage",
      "Devices",
      "Diagnostic reports",
      "Clinical documents",
      "Encounters",
      "Goals",
      "Immunizations",
      "Medications",
      "Observations",
      "Procedures",
      "Service requests",
    ],
  },
  {
    type: "athena-backend" as const,
    section: "backend" as const,
    label: "athenahealth",
    logo: "athena/athenahealth_logo.png",
    registrationUrl: "",
    scopes: [],
    description: "Population-level data from athenahealth via SMART Backend Services.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "eclinicalworks-backend" as const,
    section: "backend" as const,
    label: "eClinicalWorks",
    logo: "ecw/ecw_logo.png",
    registrationUrl: "",
    scopes: [],
    description: "Population-level data from eClinicalWorks via SMART Backend Services.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "meditech-backend" as const,
    section: "backend" as const,
    label: "MEDITECH",
    logo: "meditech/meditech_logo.jpeg",
    registrationUrl: "",
    scopes: [],
    description: "Population-level data from MEDITECH via SMART Backend Services.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "modmed-backend" as const,
    section: "backend" as const,
    label: "ModMed",
    logo: "modmed/modmed_square.jpeg",
    registrationUrl: "",
    scopes: [],
    description: "Population-level data from ModMed via SMART Backend Services.",
    supportedResources: [],
    comingSoon: true,
  },
  {
    type: "commonwell" as const,
    section: "network" as const,
    label: "CommonWell",
    logo: "commonwell/commonwell_logo.jpeg",
    registrationUrl: "",
    scopes: [],
    description: "Nationwide health data exchange via the CommonWell Health Alliance network.",
    supportedResources: [],
    comingSoon: true,
  },
] as const;

/** All provider tile types, including coming-soon placeholders. */
export type EhrProviderTileType = (typeof EHR_PROVIDERS)[number]["type"];

/** Patient-access provider types that can actually be configured — excludes coming-soon placeholders. Mirrors the server's EHR_PROVIDER_TYPES. */
export type EhrProviderType = Exclude<
  Extract<(typeof EHR_PROVIDERS)[number], { section: "patient-access" }>,
  { comingSoon: true }
>["type"];

/** Configurable clinical-workflow (EHR-embedded) provider types — excludes coming-soon placeholders. Mirrors the server's CLINICAL_WORKFLOW_PROVIDER_TYPES. */
export type EhrClinicalWorkflowType = Exclude<
  Extract<(typeof EHR_PROVIDERS)[number], { section: "clinical-workflows" }>,
  { comingSoon: true }
>["type"];

/** Configurable backend (SMART Backend Services / bulk data) provider types — excludes coming-soon placeholders. */
export type EhrBackendType = Exclude<
  Extract<(typeof EHR_PROVIDERS)[number], { section: "backend" }>,
  { comingSoon: true }
>["type"];

export const ehrProviderMap = new Map<string, (typeof EHR_PROVIDERS)[number]>(
  EHR_PROVIDERS.map((p) => [p.type, p])
);

/** Patient-access providers whose Custom Credentials card exposes a Client
 *  Secret input. Most patient-access EHRs (Epic, Cerner, athenahealth, …)
 *  authenticate via PKCE or private_key_jwt and never need a secret;
 *  add a provider type here only when it requires `client_secret_basic` /
 *  `client_secret_post` at the token endpoint. */
export const CLIENT_SECRET_PROVIDERS: readonly EhrProviderType[] = [
  "aetna",
  "meditech",
] as const;

/**
 * Vendor names for the EHR provider types above. The stored value is a
 * lowercase slug, and printing it raw put "epic" and "eclinicalworks" in front
 * of readers. Anything unlisted falls back to the slug rather than guessing at
 * capitalisation.
 */
export const EHR_PROVIDER_LABEL: Record<string, string> = {
  epic: "Epic",
  cerner: "Cerner",
  athena: "athenahealth",
  eclinicalworks: "eClinicalWorks",
  meditech: "MEDITECH",
  medicare: "Medicare",
  aetna: "Aetna",
  uhc: "UnitedHealthcare",
};
